import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './SiteHeader.css';

const SECTIONS = ['work', 'about', 'experience', 'certifications', 'contact'];

type NavLink = { id: string; label: string };

const LINKS: NavLink[] = [
  { id: 'work', label: 'Work' },
  { id: 'about', label: 'About' },
  { id: 'experience', label: 'Experience' },
  { id: 'certifications', label: 'Certifications' },
];

/**
 * Shared top bar for every page.
 *
 * The section links only exist on the landing page, so off the landing page
 * they point at "/#section" instead of "#section". That triggers a normal
 * navigation and the browser handles the scroll on arrival, which is simpler
 * and more reliable than restoring scroll position after a client side route
 * change.
 *
 * The id sits on this element rather than on a wrapper. A sticky element can
 * only slide inside its own parent, so wrapping it in a div the same height as
 * itself leaves no room to slide and it scrolls away. Cloudscape AppLayout
 * measures this same id through headerSelector.
 */
export const SiteHeader = () => {
  const { pathname } = useLocation();
  const onLanding = pathname === '/';
  const [active, setActive] = useState<string | null>(null);

  // Scroll spy only means anything where the sections actually exist.
  useEffect(() => {
    if (!onLanding) {
      setActive(null);
      return;
    }
    const targets = SECTIONS.map((id) => document.getElementById(id)).filter(
      (el): el is HTMLElement => Boolean(el)
    );
    if (!targets.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [onLanding]);

  const sectionHref = (id: string) => (onLanding ? `#${id}` : `/#${id}`);

  return (
    <header className="site-header" id="site-header">
      <div className="site-header-inner">
        {onLanding ? (
          <a className="site-brand" href="#top">
            <span className="site-brand-mark">T</span>
            <span className="site-brand-name">Tingis K</span>
          </a>
        ) : (
          <Link className="site-brand" to="/">
            <span className="site-brand-mark">T</span>
            <span className="site-brand-name">Tingis K</span>
          </Link>
        )}

        <nav className="site-nav">
          {LINKS.map((link) => (
            <a
              key={link.id}
              href={sectionHref(link.id)}
              className={active === link.id ? 'is-active' : undefined}
            >
              {link.label}
            </a>
          ))}

          {/* site-nav-keep survives the narrow width rule. Without it a phone
              visitor on the landing page has no header route to the dashboard
              at all. */}
          {onLanding ? (
            <Link className="site-nav-keep" to="/dashboard">
              Dashboard
            </Link>
          ) : (
            <span className="is-current site-nav-keep" aria-current="page">
              Dashboard
            </span>
          )}

          <a className="site-cta" href={sectionHref('contact')}>
            Get in touch
          </a>
        </nav>
      </div>
    </header>
  );
};
